import React, { useState, useEffect } from 'react';
import ConfirmDialog from './ConfirmDialog';
import StockRevertChoice from './StockRevertChoice';
import { useTranslation } from 'react-i18next';

/**
 * `askStockRevert` opts a caller into the "give the stock back / leave it as it is"
 * choice for deletions that touch warehouse balances. The answer rides along as the
 * second argument to `onConfirm`, so callers that never opted in are unaffected.
 */
const SuperAdminDeleteDialog = ({ isOpen, typeLabel, loading, onConfirm, onCancel, askStockRevert = false }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  // Bulk defaults to NOT reverting — the opposite of a single-order delete. This
  // reaches across many orders at once, and touching no inventory is the conservative
  // action at that scale: a mass revert can swing balances by an amount nobody sized
  // up first.
  const [revertStock, setRevertStock] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPassword('');
      setConfirmText('');
      setRevertStock(false);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (password.trim()) {
      setStep(2);
    }
  };

  const handleConfirm = () => {
    if (confirmText === 'DELETE') {
      onConfirm(password, { revertStock });
    }
  };

  const handlePreventPaste = (e) => {
    e.preventDefault();
  };

  const renderContent = () => {
    if (step === 1) {
      return (
        <div className="mt-2 text-sm text-gray-600 w-full">
          <p className="mb-4 text-red-600 font-semibold" style={{ wordBreak: 'break-word' }}>
            {t('superAdminDelete.warning', { typeLabel })}
          </p>
          <div className="mb-2">
            <label className="block text-sm font-semibold mb-2" style={{ textAlign: 'left' }}>{t('superAdminDelete.step1Label')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('superAdminDelete.passwordPlaceholder')}
              className="w-full px-4 py-2 border rounded"
              style={{ borderColor: '#ccc', borderRadius: '8px' }}
              autoFocus
            />
          </div>

          {askStockRevert && (
            <div className="mt-4">
              <StockRevertChoice
                variant="bulk"
                value={revertStock}
                onChange={setRevertStock}
                disabled={loading}
                name="bulk-delete-revert-stock"
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="mt-2 text-sm text-gray-600 w-full text-left">
        <h3 className="mb-2 text-base font-bold text-gray-900">
          {t('superAdminDelete.confirmDeletionHeading')}
        </h3>
        <p className="mb-4 text-gray-700">
          {t('superAdminDelete.typeToConfirmPrefix')}<span className="font-bold text-red-600">{t('superAdminDelete.typeToConfirmWord')}</span>{t('superAdminDelete.typeToConfirmSuffix')}<strong>{typeLabel}</strong>.
          <br/>
          <small className="text-gray-400 mt-1 block">{t('superAdminDelete.copyPasteDisabledNote')}</small>
        </p>

        <div className="mb-2">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onPaste={handlePreventPaste}
            onDrop={handlePreventPaste}
            onCopy={handlePreventPaste}
            placeholder={t('superAdminDelete.confirmPlaceholder')}
            autoComplete="off"
            className="w-full px-4 py-2 border rounded"
            style={{ borderColor: '#ccc', borderRadius: '8px' }}
            autoFocus
          />
        </div>

      </div>
    );
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={step === 1 ? t('superAdminDelete.titleStep1') : t('superAdminDelete.titleStep2')}
      message={renderContent()}
      confirmLabel={step === 1 ? t('superAdminDelete.nextButton') : t('superAdminDelete.confirmDeleteAllButton')}
      cancelLabel={t('superAdminDelete.cancelButton')}
      confirmVariant="danger"
      loading={loading}
      onConfirm={step === 1 ? handleNext : handleConfirm}
      onCancel={onCancel}
      confirmDisabled={step === 1 ? !password.trim() : confirmText !== 'DELETE'}
    />

  );
};

export default SuperAdminDeleteDialog;

